import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Seo from "@/components/Seo";
import { toast } from "sonner";
import {
  BookOpen, Headphones, BookMarked, Users, Heart, Search,
  Plus, X, ExternalLink, Filter, Trash2, ArrowRight, Sparkles, Pencil,
} from "lucide-react";
import { CoverField } from "@/components/CoverField";

const FORMAT_META = {
  ebook:      { label: "E-book",     icon: BookOpen,   accent: "bg-burgundy/10 text-burgundy" },
  audiobook:  { label: "Audiobook",  icon: Headphones, accent: "bg-gold/20 text-burgundy" },
  paperback:  { label: "Paperback",  icon: BookMarked, accent: "bg-jjblue/10 text-jjblue/80" },
};

function BookCard({ book, viewer, onLove, onDelete, onOpen, onEdit }) {
  const meta = FORMAT_META[book.format] || FORMAT_META.ebook;
  const Icon = meta.icon;
  const canDelete = viewer && (viewer.role === "admin" || book.uploader_id === viewer.id);
  const canEdit = viewer && (viewer.role === "admin" || book.uploader_id === viewer.id);
  return (
    <div className="jj-card overflow-hidden group flex flex-col hover:-translate-y-1 hover:shadow-xl transition-all duration-300" data-testid={`book-card-${book.id}`}>
      <button type="button" onClick={() => onOpen(book)} className="block text-left">
        <div className="relative aspect-[2/3] bg-cream-surface overflow-hidden">
          {book.cover_url ? (
            <img src={book.cover_url} alt={book.title} loading="lazy"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-burgundy/40">
              <BookOpen className="h-12 w-12" strokeWidth={1.2} />
            </div>
          )}
          <span className={`absolute top-3 left-3 inline-flex items-center gap-1 text-[10px] font-semibold tracking-wider uppercase px-2 py-1 rounded-full ${meta.accent}`}>
            <Icon className="h-3 w-3" /> {meta.label}
          </span>
        </div>
      </button>
      <div className="p-4 flex flex-col flex-1">
        <div className="text-[10px] uppercase tracking-wider text-[var(--jj-muted)]">{book.category}</div>
        <button type="button" onClick={() => onOpen(book)} className="text-left">
          <h3 className="font-serif text-lg text-jjblue mt-1 leading-tight line-clamp-2 hover:text-burgundy">{book.title}</h3>
        </button>
        <div className="mt-1 text-xs text-[var(--jj-muted)]">by {book.author}</div>
        <div className="mt-auto pt-3 flex items-center justify-between">
          <button
            onClick={() => onLove(book)}
            className={`inline-flex items-center gap-1 text-xs ${book.i_love ? "text-burgundy" : "text-[var(--jj-muted)] hover:text-burgundy"} transition-colors`}
            data-testid={`book-love-${book.id}`}
          >
            <Heart className={`h-3.5 w-3.5 ${book.i_love ? "fill-current" : ""}`} strokeWidth={1.7} /> {book.loves}
          </button>
          <div className="flex items-center gap-1">
            {book.file_url && (
              <a href={book.file_url} target="_blank" rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-xs text-burgundy hover:underline inline-flex items-center gap-1"
                data-testid={`book-open-${book.id}`}>
                Open <ExternalLink className="h-3 w-3" />
              </a>
            )}
            {canEdit && (
              <button onClick={() => onEdit(book)} className="p-1 text-[var(--jj-muted)] hover:text-burgundy" title="Edit" data-testid={`book-edit-${book.id}`}>
                <Pencil className="h-3.5 w-3.5" />
              </button>
            )}
            {canDelete && (
              <button onClick={() => onDelete(book)} className="p-1 text-[var(--jj-muted)] hover:text-burgundy" title="Remove" data-testid={`book-delete-${book.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function BookDetail({ book, onClose, onLove }) {
  if (!book) return null;
  const meta = FORMAT_META[book.format] || FORMAT_META.ebook;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-jjblue/60 backdrop-blur-sm" onClick={onClose} data-testid="book-detail-modal">
      <div className="jj-card max-w-2xl w-full max-h-[90vh] overflow-y-auto p-7 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-[var(--jj-muted)] hover:text-burgundy"><X className="h-5 w-5" /></button>
        <div className="grid sm:grid-cols-[160px,1fr] gap-6">
          <div className="aspect-[2/3] rounded-xl overflow-hidden bg-cream-surface">
            {book.cover_url ? <img src={book.cover_url} alt={book.title} className="w-full h-full object-cover" /> : (
              <div className="w-full h-full flex items-center justify-center text-burgundy/40"><BookOpen className="h-14 w-14" strokeWidth={1.2} /></div>
            )}
          </div>
          <div>
            <span className={`inline-flex items-center gap-1 text-[10px] font-semibold tracking-wider uppercase px-2 py-1 rounded-full ${meta.accent}`}>
              <meta.icon className="h-3 w-3" /> {meta.label} · {book.category}
            </span>
            <h2 className="font-serif text-3xl text-jjblue mt-3 leading-tight">{book.title}</h2>
            <div className="mt-1 text-sm text-[var(--jj-muted)]">by {book.author}</div>
            <div className="mt-1 text-xs text-[var(--jj-muted)]">shared by {book.uploader_name || "a member"}</div>
            {book.description && (
              <p className="mt-4 text-sm text-jjblue/85 leading-relaxed whitespace-pre-wrap">{book.description}</p>
            )}
            <div className="mt-6 flex items-center gap-3 flex-wrap">
              {book.file_url && (
                <a href={book.file_url} target="_blank" rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center gap-2" data-testid="book-detail-open">
                  {book.format === "audiobook" ? "Listen now" : "Read now"} <ExternalLink className="h-4 w-4" />
                </a>
              )}
              <button
                onClick={() => onLove(book)}
                className={`btn-secondary inline-flex items-center gap-2 ${book.i_love ? "text-burgundy" : ""}`}
              >
                <Heart className={`h-4 w-4 ${book.i_love ? "fill-current" : ""}`} /> {book.loves}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function UploadDialog({ open, onClose, onSaved, viewer }) {
  const [meta, setMeta] = useState({ formats: [], categories: [] });
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [description, setDescription] = useState("");
  const [coverUrl, setCoverUrl] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [format, setFormat] = useState("ebook");
  const [category, setCategory] = useState("Devotional");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedName, setUploadedName] = useState("");

  useEffect(() => {
    if (open) api.get("/books/meta").then(({ data }) => setMeta(data)).catch(() => {});
  }, [open]);

  if (!open) return null;

  const handleFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 40 * 1024 * 1024) {
      toast.error("File too large (max 40MB). Use an external link instead.");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const { data } = await api.post("/books/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setFileUrl(data.file_url);
      setUploadedName(f.name);
      toast.success("File uploaded — finish the details and send it in.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !author.trim()) {
      toast.error("Title and author are required.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/books", {
        title, author, description,
        cover_url: coverUrl.trim(),
        file_url: fileUrl.trim(),
        format, category,
      });
      onSaved(data);
      toast.success(viewer?.role === "admin" ? "Book published to the wall" : "Thank you — your book is waiting for admin review.");
      onClose();
      setTitle(""); setAuthor(""); setDescription(""); setCoverUrl(""); setFileUrl(""); setUploadedName("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit book.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-jjblue/60 backdrop-blur-sm" data-testid="book-upload-dialog">
      <div className="jj-card w-full max-w-lg max-h-[90vh] overflow-y-auto p-7 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-[var(--jj-muted)] hover:text-burgundy"><X className="h-5 w-5" /></button>
        <div className="overline text-burgundy">Share a book</div>
        <h2 className="font-serif text-2xl text-jjblue mt-2 leading-tight">Add to the Book Club wall</h2>
        <form onSubmit={submit} className="mt-5 space-y-3">
          <input className="input-jj" placeholder="Book title *" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="book-upload-title" />
          <input className="input-jj" placeholder="Author *" value={author} onChange={(e) => setAuthor(e.target.value)} data-testid="book-upload-author" />
          <div className="grid grid-cols-2 gap-3">
            <select className="input-jj" value={format} onChange={(e) => setFormat(e.target.value)} data-testid="book-upload-format">
              {(meta.formats || ["ebook", "audiobook", "paperback"]).map((f) => <option key={f} value={f}>{FORMAT_META[f]?.label || f}</option>)}
            </select>
            <select className="input-jj" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="book-upload-category">
              {(meta.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          {/* PDF/EPUB direct upload */}
          <div className="rounded-lg border border-dashed border-jj bg-cream-surface p-3">
            <label className="flex items-center justify-between gap-2 cursor-pointer">
              <div className="text-xs text-jjblue/85">
                <div className="font-medium">Upload a PDF or EPUB</div>
                <div className="text-[11px] text-[var(--jj-muted)]">Max 40MB. For audiobooks or larger files, paste an external link below.</div>
                {uploadedName && (
                  <div className="mt-1 text-[11px] text-burgundy inline-flex items-center gap-1">
                    <span className="font-medium">Uploaded:</span> {uploadedName}
                  </div>
                )}
              </div>
              <span className={`btn-secondary !py-1.5 !px-3 text-xs whitespace-nowrap ${uploading ? "opacity-60" : ""}`}>
                {uploading ? "Uploading…" : (uploadedName ? "Replace file" : "Choose file")}
              </span>
              <input
                type="file"
                accept="application/pdf,.pdf,application/epub+zip,.epub"
                onChange={handleFileChange}
                disabled={uploading}
                className="hidden"
                data-testid="book-upload-file-input"
              />
            </label>
          </div>

          <div className="relative">
            <input className="input-jj" placeholder="…or paste a public link (Drive, Dropbox, Amazon, YouTube)" value={fileUrl} onChange={(e) => setFileUrl(e.target.value)} data-testid="book-upload-fileurl" />
          </div>

          {/* Cover image — upload, generate, or paste URL */}
          <CoverField
            value={coverUrl}
            onChange={setCoverUrl}
            defaults={{ title, author }}
            testIdPrefix="book-upload-cover"
          />
          <textarea rows={4} maxLength={3000} className="input-jj" placeholder="Short description — what it's about, why the family should read it…" value={description} onChange={(e) => setDescription(e.target.value)} data-testid="book-upload-desc" />
          <div className="flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary !py-2 !px-4 text-sm">Cancel</button>
            <button disabled={busy || uploading} className="btn-primary !py-2 !px-5 text-sm" data-testid="book-upload-submit">{busy ? "Sending…" : "Send to the wall"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditBookDialog({ open, book, onClose, onSaved }) {
  const [meta, setMeta] = useState({ formats: [], categories: [] });
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [description, setDescription] = useState("");
  const [coverUrl, setCoverUrl] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [format, setFormat] = useState("ebook");
  const [category, setCategory] = useState("Devotional");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && book) {
      setTitle(book.title || "");
      setAuthor(book.author || "");
      setDescription(book.description || "");
      setCoverUrl(book.cover_url || "");
      setFileUrl(book.file_url || "");
      setFormat(book.format || "ebook");
      setCategory(book.category || "Devotional");
      api.get("/books/meta").then(({ data }) => setMeta(data)).catch(() => {});
    }
  }, [open, book]);

  if (!open || !book) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !author.trim()) { toast.error("Title and author are required."); return; }
    setBusy(true);
    try {
      const { data } = await api.patch(`/books/${book.id}`, {
        title, author, description,
        cover_url: coverUrl.trim(),
        file_url: fileUrl.trim(),
        format, category,
      });
      onSaved(data);
      toast.success("Book updated");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update book.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-jjblue/60 backdrop-blur-sm" data-testid="book-edit-dialog">
      <div className="jj-card w-full max-w-lg max-h-[90vh] overflow-y-auto p-7 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-[var(--jj-muted)] hover:text-burgundy"><X className="h-5 w-5" /></button>
        <div className="overline text-burgundy">Edit book</div>
        <h2 className="font-serif text-2xl text-jjblue mt-2 leading-tight">Update the details</h2>
        <form onSubmit={submit} className="mt-5 space-y-3">
          <input className="input-jj" placeholder="Book title *" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="book-edit-title" />
          <input className="input-jj" placeholder="Author *" value={author} onChange={(e) => setAuthor(e.target.value)} data-testid="book-edit-author" />
          <div className="grid grid-cols-2 gap-3">
            <select className="input-jj" value={format} onChange={(e) => setFormat(e.target.value)} data-testid="book-edit-format">
              {(meta.formats || ["ebook", "audiobook", "paperback"]).map((f) => <option key={f} value={f}>{FORMAT_META[f]?.label || f}</option>)}
            </select>
            <select className="input-jj" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="book-edit-category">
              {(meta.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <CoverField
            value={coverUrl}
            onChange={setCoverUrl}
            defaults={{ title, author }}
            testIdPrefix="book-edit-cover"
          />

          <input className="input-jj" placeholder="File URL (PDF/EPUB link, YouTube, Drive…)" value={fileUrl} onChange={(e) => setFileUrl(e.target.value)} data-testid="book-edit-fileurl" />
          <textarea rows={4} maxLength={3000} className="input-jj" placeholder="Description" value={description} onChange={(e) => setDescription(e.target.value)} data-testid="book-edit-desc" />
          <div className="flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary !py-2 !px-4 text-sm">Cancel</button>
            <button disabled={busy} className="btn-primary !py-2 !px-5 text-sm" data-testid="book-edit-submit">{busy ? "Saving…" : "Save changes"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CreateClubDialog({ open, onClose, onSaved, books }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [bookId, setBookId] = useState("");
  const [schedule, setSchedule] = useState("");
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Give the club a name."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/book-clubs", {
        name, description,
        book_id: bookId || null,
        meeting_schedule: schedule,
      });
      onSaved(data);
      toast.success("Book club started");
      onClose();
      setName(""); setDescription(""); setBookId(""); setSchedule("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create club.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-jjblue/60 backdrop-blur-sm" data-testid="club-create-dialog">
      <div className="jj-card w-full max-w-lg p-7 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-[var(--jj-muted)] hover:text-burgundy"><X className="h-5 w-5" /></button>
        <div className="overline text-burgundy">Start a book club</div>
        <h2 className="font-serif text-2xl text-jjblue mt-2 leading-tight">Gather readers around a book.</h2>
        <form onSubmit={submit} className="mt-5 space-y-3">
          <input className="input-jj" placeholder="Club name *" value={name} onChange={(e) => setName(e.target.value)} data-testid="club-create-name" />
          <textarea rows={3} maxLength={2000} className="input-jj" placeholder="What are you reading and why? (optional)" value={description} onChange={(e) => setDescription(e.target.value)} data-testid="club-create-desc" />
          <select className="input-jj" value={bookId} onChange={(e) => setBookId(e.target.value)} data-testid="club-create-book">
            <option value="">Focus book (optional)</option>
            {books.map((b) => <option key={b.id} value={b.id}>{b.title} — {b.author}</option>)}
          </select>
          <input className="input-jj" placeholder="Meeting schedule (e.g. Wednesdays 8pm ET on Zoom)" value={schedule} onChange={(e) => setSchedule(e.target.value)} data-testid="club-create-schedule" />
          <div className="flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary !py-2 !px-4 text-sm">Cancel</button>
            <button disabled={busy} className="btn-primary !py-2 !px-5 text-sm" data-testid="club-create-submit">{busy ? "Starting…" : "Start the club"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ClubCard({ club, onJoin, onLeave, viewer }) {
  return (
    <div className="jj-card p-5 flex gap-4 items-start" data-testid={`club-card-${club.id}`}>
      <div className="h-20 w-14 shrink-0 rounded-md bg-cream-surface overflow-hidden">
        {club.book_cover_url ? <img src={club.book_cover_url} alt="" className="w-full h-full object-cover" /> : (
          <div className="w-full h-full flex items-center justify-center text-burgundy/40"><Users className="h-6 w-6" strokeWidth={1.4} /></div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <Link to={`/book-club/clubs/${club.id}`} className="block">
          <h3 className="font-serif text-lg text-jjblue leading-tight hover:text-burgundy">{club.name}</h3>
        </Link>
        {club.book_title && <div className="text-xs text-[var(--jj-muted)] mt-0.5">Reading: <span className="text-jjblue/85">{club.book_title}</span></div>}
        {club.meeting_schedule && <div className="text-xs text-[var(--jj-muted)] mt-0.5">{club.meeting_schedule}</div>}
        <div className="mt-3 flex items-center justify-between">
          <span className="text-xs text-[var(--jj-muted)]"><Users className="inline h-3 w-3 mr-1" />{club.member_count}</span>
          {viewer ? (
            club.i_joined ? (
              <button onClick={() => onLeave(club)} className="btn-secondary !py-1.5 !px-3 text-xs" data-testid={`club-leave-${club.id}`}>Leave</button>
            ) : (
              <button onClick={() => onJoin(club)} className="btn-primary !py-1.5 !px-3 text-xs" data-testid={`club-join-${club.id}`}>Join</button>
            )
          ) : (
            <Link to="/login" className="text-xs text-burgundy hover:underline">Sign in to join</Link>
          )}
        </div>
      </div>
    </div>
  );
}

export default function BookClub() {
  const { user } = useAuth();
  const [tab, setTab] = useState("library"); // 'library' | 'clubs'
  const [books, setBooks] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [createClubOpen, setCreateClubOpen] = useState(false);
  const [editingBook, setEditingBook] = useState(null);
  const [format, setFormat] = useState("all");
  const [category, setCategory] = useState("all");
  const [query, setQuery] = useState("");
  const [meta, setMeta] = useState({ formats: [], categories: [] });

  const load = async () => {
    setLoading(true);
    try {
      const [b, c, m] = await Promise.all([
        api.get("/books", { params: { limit: 200 } }),
        api.get("/book-clubs"),
        api.get("/books/meta"),
      ]);
      setBooks(b.data || []);
      setClubs(c.data || []);
      setMeta(m.data || {});
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return books.filter((b) => {
      if (format !== "all" && b.format !== format) return false;
      if (category !== "all" && b.category !== category) return false;
      if (!q) return true;
      return (b.title + " " + b.author + " " + (b.description || "")).toLowerCase().includes(q);
    });
  }, [books, format, category, query]);

  const loveBook = async (book) => {
    if (!user) { toast.error("Sign in to love a book."); return; }
    try {
      const { data } = await api.post(`/books/${book.id}/love`);
      const upd = (arr) => arr.map((b) => b.id === book.id ? { ...b, loves: data.loves, i_love: data.i_love } : b);
      setBooks(upd);
      setSelected((s) => s && s.id === book.id ? { ...s, ...data } : s);
    } catch { /* silent */ }
  };

  const deleteBook = async (book) => {
    if (!window.confirm(`Remove "${book.title}" from the wall?`)) return;
    try {
      await api.delete(`/books/${book.id}`);
      setBooks((arr) => arr.filter((b) => b.id !== book.id));
      if (selected?.id === book.id) setSelected(null);
      toast.success("Removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove");
    }
  };

  const joinClub = async (club) => {
    try {
      const { data } = await api.post(`/book-clubs/${club.id}/join`);
      setClubs((arr) => arr.map((c) => c.id === club.id ? data : c));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not join"); }
  };
  const leaveClub = async (club) => {
    try {
      const { data } = await api.post(`/book-clubs/${club.id}/leave`);
      setClubs((arr) => arr.map((c) => c.id === club.id ? data : c));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not leave"); }
  };

  return (
    <div className="bg-cream min-h-[calc(100vh-5rem)]">
      <Seo
        title="Book Club · Jesus Just Us"
        description="A library of e-books and audiobooks shared by the JJU family. Read, listen, upload your own for exposure, and join book clubs discussing what's on our shelves."
        path="/book-club"
      />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 pt-14 pb-8">
        <div className="overline text-burgundy flex items-center gap-2"><BookOpen className="h-3.5 w-3.5" /> Book Club</div>
        <h1 className="font-serif text-5xl lg:text-6xl text-jjblue mt-3 leading-tight">
          Read together.<br /><span className="text-burgundy italic">Grow together.</span>
        </h1>
        <p className="mt-5 max-w-2xl text-lg text-[var(--jj-text-secondary)] leading-relaxed">
          A living library of e-books and audiobooks shared by the JJU family. Read what others have gifted, share your own, and gather in clubs to walk through a book with a circle of believers.
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button onClick={() => user ? setUploadOpen(true) : toast.error("Sign in to share a book.")} className="btn-primary inline-flex items-center gap-2" data-testid="book-upload-open">
            <Plus className="h-4 w-4" /> Share a book
          </button>
          <button onClick={() => user ? setCreateClubOpen(true) : toast.error("Sign in to start a club.")} className="btn-secondary inline-flex items-center gap-2" data-testid="club-create-open">
            <Users className="h-4 w-4" /> Start a book club
          </button>
          <span className="text-xs text-[var(--jj-muted)] inline-flex items-center gap-1.5">
            <Sparkles className="h-3 w-3 text-gold" /> {books.length} book{books.length === 1 ? "" : "s"} · {clubs.length} club{clubs.length === 1 ? "" : "s"}
          </span>
        </div>
      </section>

      {/* Tabs */}
      <section className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="inline-flex bg-cream-surface rounded-full p-1 text-sm" data-testid="book-tabs">
          <button onClick={() => setTab("library")} className={`px-4 py-1.5 rounded-full ${tab === "library" ? "bg-burgundy text-cream" : "text-jjblue/70"}`} data-testid="book-tab-library">Library</button>
          <button onClick={() => setTab("clubs")} className={`px-4 py-1.5 rounded-full ${tab === "clubs" ? "bg-burgundy text-cream" : "text-jjblue/70"}`} data-testid="book-tab-clubs">Clubs</button>
        </div>
      </section>

      {tab === "library" && (
        <>
          {/* Filters */}
          <section className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 pt-6">
            <div className="flex flex-wrap items-center gap-3 justify-between">
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => setFormat("all")} className={`text-xs px-3 py-1.5 rounded-full ${format === "all" ? "bg-burgundy text-cream" : "bg-cream-surface border border-jj text-jjblue/80"}`} data-testid="book-format-all">All formats</button>
                {(meta.formats || []).map((f) => (
                  <button key={f} onClick={() => setFormat(f)} className={`text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1 ${format === f ? "bg-burgundy text-cream" : "bg-cream-surface border border-jj text-jjblue/80"}`} data-testid={`book-format-${f}`}>
                    {FORMAT_META[f]?.label || f}
                  </button>
                ))}
                <span className="mx-2 text-[var(--jj-muted)]">·</span>
                <select value={category} onChange={(e) => setCategory(e.target.value)} className="text-xs bg-cream-surface border border-jj rounded-full px-3 py-1.5" data-testid="book-category-select">
                  <option value="all">All categories</option>
                  {(meta.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="relative w-full sm:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--jj-muted)]" />
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search titles, authors…" className="input-jj pl-10" data-testid="book-search" />
              </div>
            </div>
          </section>

          {/* Grid */}
          <section className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-8">
            {loading ? (
              <div className="text-center py-16 text-[var(--jj-muted)]">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16" data-testid="book-empty">
                <BookOpen className="h-10 w-10 text-burgundy mx-auto" strokeWidth={1.2} />
                <p className="mt-4 text-sm text-[var(--jj-text-secondary)] max-w-md mx-auto">
                  {books.length === 0
                    ? "The wall is empty — be the first to share a book with the family."
                    : "No books match that filter yet."}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="book-grid">
                {filtered.map((b) => (
                  <BookCard key={b.id} book={b} viewer={user} onLove={loveBook} onDelete={deleteBook} onOpen={setSelected} onEdit={setEditingBook} />
                ))}
              </div>
            )}
          </section>
        </>
      )}

      {tab === "clubs" && (
        <section className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-8">
          {loading ? (
            <div className="text-center py-16 text-[var(--jj-muted)]">Loading…</div>
          ) : clubs.length === 0 ? (
            <div className="text-center py-16" data-testid="club-empty">
              <Users className="h-10 w-10 text-burgundy mx-auto" strokeWidth={1.2} />
              <p className="mt-4 text-sm text-[var(--jj-text-secondary)] max-w-md mx-auto">No book clubs yet — start the first one and gather readers around a book.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="club-grid">
              {clubs.map((c) => <ClubCard key={c.id} club={c} onJoin={joinClub} onLeave={leaveClub} viewer={user} />)}
            </div>
          )}
        </section>
      )}

      <section className="max-w-3xl mx-auto px-6 pb-16 text-center">
        <p className="text-sm text-[var(--jj-muted)]">
          Part of the <Link to="/family" className="text-burgundy font-medium hover:underline">Jesus Just Us family</Link>.
        </p>
      </section>

      <UploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} viewer={user} onSaved={(book) => book.status === "approved" && setBooks((arr) => [book, ...arr])} />
      <EditBookDialog
        open={!!editingBook}
        book={editingBook}
        onClose={() => setEditingBook(null)}
        onSaved={(book) => setBooks((arr) => arr.map((b) => (b.id === book.id ? book : b)))}
      />
      <CreateClubDialog open={createClubOpen} onClose={() => setCreateClubOpen(false)} books={books} onSaved={(club) => setClubs((arr) => [club, ...arr])} />
      <BookDetail book={selected} onClose={() => setSelected(null)} onLove={loveBook} />
    </div>
  );
}
